import { Controller } from "@hotwired/stimulus"

// Handles the state-aware subscription date selection modal.
// Radio-card option selection, offline date picker, total bar, and form field management.
// All state-specific content is rendered server-side; this controller is state-agnostic.
export default class extends Controller {
  static targets = [
    "modal", "backdrop", "panel",
    "optionCard",
    "startDateField", "endDateField", "nextMonthField",
    "customDateSection", "customStartDate", "calendarPicker", "coveredUntil",
    "customAmountDisplay", "customCreditDisplay", "customCreditText",
    "totalAmountDisplay", "totalMonthlyFee", "totalDateRange",
    "submitBtn",
    // Past payment (active subscription state) targets
    "pastStartDate", "pastCalendarPicker", "pastCoveredUntil",
    "pastTotalAmount", "pastTotalDateRange", "pastTotalMonthlyFee",
    "pastSummary",
    "pastStartDateField", "pastSubmitBtn"
  ]

  // previewUrl: endpoint for live amount/date previews (admin overrides it to resolve for the
  // selected student). autoOpen: open immediately on connect (admin injects the modal already-open).
  static values = {
    planId: Number,
    currency: String,
    simulateToday: String,
    previewUrl: { type: String, default: "/my/subscriptions/date_preview" },
    autoOpen: Boolean
  }

  // CSS class sets for option cards
  static SELECTED_CARD = ["border-2", "border-emerald-600", "bg-emerald-50"]
  static DEFAULT_CARD = ["border", "border-gray-200", "bg-white"]
  static SELECTED_RADIO_BORDER = ["border-emerald-600", "bg-emerald-600"]
  static DEFAULT_RADIO_BORDER = ["border-gray-300"]
  static SELECTED_TITLE = ["text-emerald-900"]
  static DEFAULT_TITLE = ["text-gray-900"]
  static SELECTED_SUB = ["text-emerald-700"]
  static DEFAULT_SUB = ["text-gray-500"]
  static SELECTED_HINT = ["text-emerald-700"]
  static DEFAULT_HINT = ["text-gray-400"]

  connect() {
    if (this.autoOpenValue) {
      this.open()
      this.#emitInitialSelection()
    }
  }

  // The server pre-selects the recommended card; surface its dates to the embedder (admin
  // wizard) so confirming without re-clicking still applies the default option.
  #emitInitialSelection() {
    const selected = this.optionCardTargets.find((c) => c.classList.contains("border-emerald-600"))
    if (!selected || selected.dataset.hasPicker === "true") return
    this.#emitChange({
      startDate: selected.dataset.startDate, endDate: selected.dataset.endDate,
      nextMonth: selected.dataset.nextMonth || "",
      amountCents: selected.dataset.amountCents, monthlyFeeCents: selected.dataset.monthlyFeeCents
    })
  }

  toggle(event) {
    event.preventDefault()
    if (this.modalTarget.classList.contains("hidden")) {
      this.open()
    } else {
      this.close()
    }
  }

  open() {
    this.modalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
    requestAnimationFrame(() => {
      this.backdropTarget.classList.add("opacity-100")
      this.backdropTarget.classList.remove("opacity-0")
      this.panelTarget.classList.add("survey-enter")
    })
  }

  close() {
    this.backdropTarget.classList.add("opacity-0")
    this.backdropTarget.classList.remove("opacity-100")
    this.panelTarget.classList.remove("survey-enter")
    setTimeout(() => {
      this.modalTarget.classList.add("hidden")
      document.body.style.overflow = ""
    }, 300)
  }

  selectOption(event) {
    const card = event.currentTarget

    // Update visual state of all option cards
    this.optionCardTargets.forEach((c) => this.#deselectCard(c))
    this.#selectCard(card)

    // Read data attributes from selected card
    const startDate = card.dataset.startDate
    const endDate = card.dataset.endDate
    const nextMonth = card.dataset.nextMonth || ""
    const summary = card.dataset.summary
    const hasPicker = card.dataset.hasPicker === "true"
    const maxDate = card.dataset.maxDate
    const amountCents = card.dataset.amountCents
    const monthlyFeeCents = card.dataset.monthlyFeeCents

    // Update hidden form fields
    if (this.hasStartDateFieldTarget) this.startDateFieldTarget.value = startDate
    if (this.hasEndDateFieldTarget) this.endDateFieldTarget.value = endDate || ""
    if (this.hasNextMonthFieldTarget) this.nextMonthFieldTarget.value = nextMonth

    if (hasPicker) {
      this.#showPicker(maxDate)
      this.#clearTotalBar()
      this.disableSubmit()
      this.#emitChange(null)
    } else {
      this.#hidePicker()
      this.#updateTotalBar(amountCents, monthlyFeeCents, startDate, endDate)
      this.enableSubmit()
      this.#emitChange({ startDate, endDate, nextMonth, amountCents, monthlyFeeCents })
    }
  }

  // Notifies an embedding controller (the admin wizard) of the current valid selection, or
  // null when the selection is incomplete (picker shown, awaiting a date). No-op for students.
  #emitChange(detail) {
    this.dispatch("change", { detail: detail || { valid: false } })
  }

  openCalendar() {
    if (this.hasCalendarPickerTarget) this.calendarPickerTarget.showPicker()
  }

  calendarPicked() {
    const iso = this.calendarPickerTarget.value
    if (!iso) return
    const [yyyy, mm, dd] = iso.split("-")
    this.customStartDateTarget.value = `${dd}/${mm}/${yyyy}`
    this.#updateFromManualDate()
  }

  formatDateInput(event) {
    let v = event.target.value.replace(/\D/g, "")
    if (v.length > 2) v = v.slice(0, 2) + "/" + v.slice(2)
    if (v.length > 5) v = v.slice(0, 5) + "/" + v.slice(5)
    event.target.value = v.slice(0, 10)
    this.#updateFromManualDate()
  }

  enableSubmit() {
    this.submitBtnTargets.forEach((btn) => {
      btn.disabled = false
      btn.classList.remove("opacity-50", "cursor-not-allowed")
    })
  }

  disableSubmit() {
    this.submitBtnTargets.forEach((btn) => {
      btn.disabled = true
      btn.classList.add("opacity-50", "cursor-not-allowed")
    })
  }

  // --- Past payment (active subscription state) ---

  openPastCalendar() {
    if (this.hasPastCalendarPickerTarget) this.pastCalendarPickerTarget.showPicker()
  }

  pastCalendarPicked() {
    const iso = this.pastCalendarPickerTarget.value
    if (!iso) return
    const [yyyy, mm, dd] = iso.split("-")
    this.pastStartDateTarget.value = `${dd}/${mm}/${yyyy}`
    this.#updateFromPastDate()
  }

  formatPastDateInput(event) {
    let v = event.target.value.replace(/\D/g, "")
    if (v.length > 2) v = v.slice(0, 2) + "/" + v.slice(2)
    if (v.length > 5) v = v.slice(0, 5) + "/" + v.slice(5)
    event.target.value = v.slice(0, 10)
    this.#updateFromPastDate()
  }

  // --- Private helpers ---

  #selectCard(card) {
    // Card border/bg
    this.constructor.DEFAULT_CARD.forEach((c) => card.classList.remove(c))
    this.constructor.SELECTED_CARD.forEach((c) => card.classList.add(c))

    // Radio circle
    const radio = card.querySelector(".option-card-radio")
    if (radio) {
      this.constructor.DEFAULT_RADIO_BORDER.forEach((c) => radio.classList.remove(c))
      this.constructor.SELECTED_RADIO_BORDER.forEach((c) => radio.classList.add(c))
      const dot = radio.querySelector(".option-card-dot")
      if (dot) dot.classList.add("bg-white")
    }

    // Text colors
    const title = card.querySelector(".opt-title, p:first-child")
    if (title) {
      this.constructor.DEFAULT_TITLE.forEach((c) => title.classList.remove(c))
      this.constructor.SELECTED_TITLE.forEach((c) => title.classList.add(c))
    }

    // Sub and hint text
    card.querySelectorAll("p").forEach((p, i) => {
      if (i === 1) { // sub
        this.constructor.DEFAULT_SUB.forEach((c) => p.classList.remove(c))
        this.constructor.SELECTED_SUB.forEach((c) => p.classList.add(c))
      }
      if (i === 2) { // hint
        this.constructor.DEFAULT_HINT.forEach((c) => p.classList.remove(c))
        this.constructor.SELECTED_HINT.forEach((c) => p.classList.add(c))
      }
    })
  }

  #deselectCard(card) {
    // Card border/bg
    this.constructor.SELECTED_CARD.forEach((c) => card.classList.remove(c))
    this.constructor.DEFAULT_CARD.forEach((c) => card.classList.add(c))

    // Radio circle
    const radio = card.querySelector(".option-card-radio")
    if (radio) {
      this.constructor.SELECTED_RADIO_BORDER.forEach((c) => radio.classList.remove(c))
      this.constructor.DEFAULT_RADIO_BORDER.forEach((c) => radio.classList.add(c))
      const dot = radio.querySelector(".option-card-dot")
      if (dot) dot.classList.remove("bg-white")
    }

    // Text colors
    const title = card.querySelector(".opt-title, p:first-child")
    if (title) {
      this.constructor.SELECTED_TITLE.forEach((c) => title.classList.remove(c))
      this.constructor.DEFAULT_TITLE.forEach((c) => title.classList.add(c))
    }

    card.querySelectorAll("p").forEach((p, i) => {
      if (i === 1) {
        this.constructor.SELECTED_SUB.forEach((c) => p.classList.remove(c))
        this.constructor.DEFAULT_SUB.forEach((c) => p.classList.add(c))
      }
      if (i === 2) {
        this.constructor.SELECTED_HINT.forEach((c) => p.classList.remove(c))
        this.constructor.DEFAULT_HINT.forEach((c) => p.classList.add(c))
      }
    })
  }

  #updateTotalBar(amountCents, monthlyFeeCents, startDateIso, endDateIso) {
    if (this.hasTotalAmountDisplayTarget && amountCents) {
      this.totalAmountDisplayTarget.textContent = this.#formatAmount(parseInt(amountCents, 10))
    }
    if (this.hasTotalMonthlyFeeTarget && monthlyFeeCents) {
      this.totalMonthlyFeeTarget.textContent =
        parseInt(amountCents, 10) !== parseInt(monthlyFeeCents, 10)
          ? `${this.#formatAmount(parseInt(monthlyFeeCents, 10))}/mo pro-rated`
          : ""
    }
    if (this.hasTotalDateRangeTarget && startDateIso && endDateIso) {
      const start = new Date(startDateIso + "T00:00:00")
      const end = new Date(endDateIso + "T00:00:00")
      this.totalDateRangeTarget.textContent = `${this.#formatDisplay(start)} → ${this.#formatDisplay(end)}`
    }
  }

  #clearTotalBar() {
    if (this.hasTotalAmountDisplayTarget) this.totalAmountDisplayTarget.textContent = "—"
    if (this.hasTotalMonthlyFeeTarget) this.totalMonthlyFeeTarget.textContent = ""
    if (this.hasTotalDateRangeTarget) this.totalDateRangeTarget.textContent = "—"
  }

  #showPicker(maxDate) {
    if (this.hasCustomDateSectionTarget) {
      this.customDateSectionTarget.classList.remove("hidden")
    }
    // Hide the "Amount calculated once you pick a date." footnote
    if (this.hasCustomAmountDisplayTarget) {
      this.customAmountDisplayTarget.classList.add("hidden")
    }
    if (this.hasCalendarPickerTarget && maxDate) {
      this.calendarPickerTarget.max = maxDate
    }
    // Reset picker fields
    if (this.hasCustomStartDateTarget) this.customStartDateTarget.value = ""
    if (this.hasCalendarPickerTarget) this.calendarPickerTarget.value = ""
    if (this.hasCoveredUntilTarget) this.coveredUntilTarget.innerHTML = "&mdash;"
    this.#renderCustomCredit(null)
    // The section opens below the fold inside the scrollable panel — bring it into view.
    if (this.hasCustomDateSectionTarget) {
      requestAnimationFrame(() => {
        this.customDateSectionTarget.scrollIntoView({ behavior: "smooth", block: "nearest" })
      })
    }
  }

  #hidePicker() {
    if (this.hasCustomDateSectionTarget) {
      this.customDateSectionTarget.classList.add("hidden")
    }
    // Restore the footnote when switching away from picker card
    if (this.hasCustomAmountDisplayTarget) {
      this.customAmountDisplayTarget.classList.remove("hidden")
    }
  }

  #updateFromManualDate() {
    const raw = this.customStartDateTarget.value.trim()

    // Hide any stale credit badge while the date is incomplete/invalid
    this.#renderCustomCredit(null)

    if (!raw || raw.length < 10) {
      this.startDateFieldTarget.value = ""
      this.#clearTotalBar()
      this.disableSubmit()
      return
    }

    const parts = raw.split("/")
    const dd = parseInt(parts[0], 10)
    const mm = parseInt(parts[1], 10)
    const yyyy = parseInt(parts[2], 10)

    const startDate = new Date(yyyy, mm - 1, dd)
    if (
      Number.isNaN(startDate.getTime()) ||
      startDate.getFullYear() !== yyyy ||
      startDate.getMonth() !== mm - 1 ||
      startDate.getDate() !== dd
    ) {
      this.startDateFieldTarget.value = ""
      this.#clearTotalBar()
      this.disableSubmit()
      return
    }

    // Enforce max date from calendar picker
    if (this.hasCalendarPickerTarget && this.calendarPickerTarget.max) {
      const maxDate = new Date(this.calendarPickerTarget.max + "T00:00:00")
      if (startDate > maxDate) {
        this.startDateFieldTarget.value = ""
        this.#clearTotalBar()
        this.disableSubmit()
        return
      }
    }

    const today = new Date()
    today.setHours(0, 0, 0, 0)
    if (startDate > today) {
      this.startDateFieldTarget.value = ""
      this.#clearTotalBar()
      this.disableSubmit()
      return
    }

    // End date is always the last day of the start date's calendar month
    const endDate = this.#endOfMonth(startDate)

    this.startDateFieldTarget.value = this.#formatIso(startDate)
    if (this.hasNextMonthFieldTarget) this.nextMonthFieldTarget.value = ""

    // Update "Covered until" display
    if (this.hasCoveredUntilTarget) {
      this.coveredUntilTarget.textContent = this.#coveredUntilLabel(startDate, endDate)
    }

    // Update footer date range live (amount updates after fetch)
    if (this.hasTotalDateRangeTarget) {
      this.totalDateRangeTarget.textContent = `${this.#formatDisplay(startDate)} → ${this.#formatDisplay(endDate)}`
    }
    if (this.hasTotalAmountDisplayTarget) this.totalAmountDisplayTarget.textContent = "…"
    if (this.hasTotalMonthlyFeeTarget) this.totalMonthlyFeeTarget.textContent = ""

    this.enableSubmit()
    this.#fetchAndDisplayAmount(this.#formatIso(startDate), "custom")
  }

  #endOfMonth(date) {
    // Last day of the same calendar month as date
    return new Date(date.getFullYear(), date.getMonth() + 1, 0)
  }

  // Inclusive number of days between two dates (e.g. 1st → 30th = 30 days).
  #daysInclusive(start, end) {
    return Math.round((end - start) / 86400000) + 1
  }

  // "30/06/2026 (19 days)" — numeric to match the dd/mm/yyyy start-date input beside it.
  #coveredUntilLabel(start, end) {
    const days = this.#daysInclusive(start, end)
    return `${this.#formatNumeric(end)} (${days} ${days === 1 ? "day" : "days"})`
  }

  #formatDisplay(date) {
    return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
  }

  // dd/mm/yyyy — matches the masked date inputs.
  #formatNumeric(date) {
    const dd = String(date.getDate()).padStart(2, "0")
    const mm = String(date.getMonth() + 1).padStart(2, "0")
    return `${dd}/${mm}/${date.getFullYear()}`
  }

  #formatIso(date) {
    const yyyy = date.getFullYear()
    const mm = String(date.getMonth() + 1).padStart(2, "0")
    const dd = String(date.getDate()).padStart(2, "0")
    return `${yyyy}-${mm}-${dd}`
  }

  // Scrolls the modal panel to its very bottom so the confirm button and the whitespace
  // beneath it are fully revealed. Runs on a short delay so it lands after the layout has
  // settled and after the browser's own focus-into-view scroll from the date picker.
  #scrollPanelToBottom() {
    if (!this.hasPanelTarget) return
    setTimeout(() => {
      this.panelTarget.scrollTo({ top: this.panelTarget.scrollHeight, behavior: "smooth" })
    }, 120)
  }

  #formatAmount(cents) {
    const currency = this.hasCurrencyValue ? this.currencyValue : "INR"
    const amount = new Intl.NumberFormat("en-IN").format(Math.floor(cents / 100))
    return `${currency} ${amount}`
  }

  #fetchAndDisplayAmount(isoDate, target) {
    if (!this.hasPlanIdValue) return
    const url = new URL(this.previewUrlValue, window.location.origin)
    url.searchParams.set("plan_id", this.planIdValue)
    url.searchParams.set("start_date", isoDate)
    if (this.hasSimulateTodayValue) url.searchParams.set("simulate_today", this.simulateTodayValue)
    fetch(url.toString())
      .then(r => r.json())
      .then(data => {
        if (target === "custom") {
          if (data.error) {
            this.#clearTotalBar()
            this.#renderCustomCredit(null)
            this.#emitChange(null)
            return
          }
          // Update footer total bar with fetched amount
          if (this.hasTotalAmountDisplayTarget) {
            this.totalAmountDisplayTarget.textContent = this.#formatAmount(data.amount_cents)
          }
          if (this.hasTotalMonthlyFeeTarget) {
            this.totalMonthlyFeeTarget.textContent =
              data.amount_cents !== data.monthly_fee_cents
                ? `${this.#formatAmount(data.monthly_fee_cents)}/mo pro-rated`
                : ""
          }
          this.#renderCustomCredit(data.classes_credited)
          if (this.hasEndDateFieldTarget && data.end_date) this.endDateFieldTarget.value = data.end_date
          this.#emitChange({
            startDate: data.start_date || isoDate, endDate: data.end_date, nextMonth: "",
            amountCents: data.amount_cents, monthlyFeeCents: data.monthly_fee_cents
          })
          // The filled total bar pushes the confirm button below the fold — scroll the panel
          // fully to the bottom so the button and the whitespace beneath it are visible. Deferred
          // past layout settle and the native date-picker's focus scroll, which fight an rAF.
          this.#scrollPanelToBottom()
        } else if (target === "past") {
          if (data.error) {
            this.#clearPastTotalBar()
            this.#emitChange(null)
            return
          }
          // The server may cap the covered period so sync the displayed dates
          const start = data.start_date ? new Date(data.start_date + "T00:00:00") : null
          const end = data.end_date ? new Date(data.end_date + "T00:00:00") : null
          if (start && end && this.hasPastCoveredUntilTarget) {
            this.pastCoveredUntilTarget.textContent = this.#coveredUntilLabel(start, end)
          }
          this.#updatePastTotalBar(data.amount_cents, data.monthly_fee_cents, start, end)
          this.#emitChange({
            startDate: data.start_date, endDate: data.end_date, nextMonth: "",
            amountCents: data.amount_cents, monthlyFeeCents: data.monthly_fee_cents
          })
        }
      })
      .catch(() => {
        if (target === "custom") {
          this.#clearTotalBar()
          this.#renderCustomCredit(null)
        }
      })
  }

  // Shows/updates the pro-rated class-credit badge for the offline date picker. A nil/0
  // count (unlimited plan or no valid date) hides it.
  #renderCustomCredit(count) {
    if (!this.hasCustomCreditDisplayTarget) return
    const c = parseInt(count, 10)
    if (Number.isInteger(c) && c > 0) {
      if (this.hasCustomCreditTextTarget) {
        this.customCreditTextTarget.textContent = `${c} ${c === 1 ? "class" : "classes"} credited for this period`
      }
      this.customCreditDisplayTarget.classList.remove("hidden")
    } else {
      this.customCreditDisplayTarget.classList.add("hidden")
    }
  }

  #updateFromPastDate() {
    const raw = this.pastStartDateTarget.value.trim()
    const summaryEl = this.pastSummaryTarget

    const disable = () => {
      this.pastSubmitBtnTargets.forEach((btn) => {
        btn.disabled = true
        btn.classList.add("opacity-50", "cursor-not-allowed")
      })
    }
    const enable = () => {
      this.pastSubmitBtnTargets.forEach((btn) => {
        btn.disabled = false
        btn.classList.remove("opacity-50", "cursor-not-allowed")
      })
    }

    if (!raw || raw.length < 10) {
      this.pastStartDateFieldTarget.value = ""
      this.#clearPastTotalBar()
      summaryEl.textContent = raw.length > 0 ? "Enter date as dd/mm/yyyy." : "Pick a start date above"
      disable()
      return
    }

    const parts = raw.split("/")
    const dd = parseInt(parts[0], 10)
    const mm = parseInt(parts[1], 10)
    const yyyy = parseInt(parts[2], 10)
    const startDate = new Date(yyyy, mm - 1, dd)

    if (
      Number.isNaN(startDate.getTime()) ||
      startDate.getFullYear() !== yyyy ||
      startDate.getMonth() !== mm - 1 ||
      startDate.getDate() !== dd
    ) {
      this.pastStartDateFieldTarget.value = ""
      this.#clearPastTotalBar()
      summaryEl.textContent = "Enter a valid date as dd/mm/yyyy."
      disable()
      return
    }

    // Enforce max date
    if (this.hasPastCalendarPickerTarget && this.pastCalendarPickerTarget.max) {
      const maxDate = new Date(this.pastCalendarPickerTarget.max + "T00:00:00")
      if (startDate > maxDate) {
        this.pastStartDateFieldTarget.value = ""
        this.#clearPastTotalBar()
        summaryEl.textContent = "Date cannot be after " + this.#formatDisplay(maxDate) + "."
        disable()
        return
      }
    }

    // Must be in the past
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    if (startDate >= today) {
      this.pastStartDateFieldTarget.value = ""
      this.#clearPastTotalBar()
      summaryEl.textContent = "Start date must be in the past."
      disable()
      return
    }

    // End date is always the last day of the start date's calendar month
    const endDate = this.#endOfMonth(startDate)

    this.pastStartDateFieldTarget.value = this.#formatIso(startDate)

    if (this.hasPastCoveredUntilTarget) {
      this.pastCoveredUntilTarget.textContent = this.#coveredUntilLabel(startDate, endDate)
    }

    // Total bar carries the date range; the validation line clears, amount fills after fetch.
    summaryEl.textContent = ""
    if (this.hasPastTotalDateRangeTarget) {
      this.pastTotalDateRangeTarget.textContent = `${this.#formatDisplay(startDate)} → ${this.#formatDisplay(endDate)}`
    }
    if (this.hasPastTotalAmountTarget) this.pastTotalAmountTarget.textContent = "…"
    if (this.hasPastTotalMonthlyFeeTarget) this.pastTotalMonthlyFeeTarget.textContent = ""
    enable()
    this.#fetchAndDisplayAmount(this.#formatIso(startDate), "past")
  }

  #updatePastTotalBar(amountCents, monthlyFeeCents, startDate, endDate) {
    if (this.hasPastTotalAmountTarget && amountCents != null) {
      this.pastTotalAmountTarget.textContent = this.#formatAmount(amountCents)
    }
    if (this.hasPastTotalMonthlyFeeTarget && monthlyFeeCents != null) {
      this.pastTotalMonthlyFeeTarget.textContent =
        amountCents !== monthlyFeeCents ? `${this.#formatAmount(monthlyFeeCents)}/mo pro-rated` : ""
    }
    if (this.hasPastTotalDateRangeTarget && startDate && endDate) {
      this.pastTotalDateRangeTarget.textContent = `${this.#formatDisplay(startDate)} → ${this.#formatDisplay(endDate)}`
    }
  }

  #clearPastTotalBar() {
    if (this.hasPastTotalAmountTarget) this.pastTotalAmountTarget.textContent = "—"
    if (this.hasPastTotalMonthlyFeeTarget) this.pastTotalMonthlyFeeTarget.textContent = ""
    if (this.hasPastTotalDateRangeTarget) this.pastTotalDateRangeTarget.textContent = "—"
  }
}
